import { Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	AgGridEvent,
	ColDef,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
	themeMaterial,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { isNil } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../../../components/modals/error-modal";
import { Action } from "../../../models/Action";
import { getActions } from "../../../repositories/ProfilesRepository";

export const searchActions = () => {
	return useQuery<Action[], Error>({
		queryKey: ["actions"],
		queryFn: ({ signal }) => getActions(signal),
	});
};

interface Props {
	actionsOfProfile: Action[];
	onSelectionChanged: Function;
}

export const DataTable = ({ actionsOfProfile, onSelectionChanged }: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const sortRows = (event: AgGridEvent) => {
		event.api.applyColumnState({
			state: [{ colId: "ActionName", sort: "asc", sortIndex: 0 }],
		});
	};

	const { data: rows, isLoading, isError, error: e } = searchActions();

	const formattedRows = rows?.map(({ ActionId, ActionName }) => {
		return {
			ActionId: ActionId,
			ActionName: ActionName,
		};
	});
	const columns: ColDef[] = [
		{
			field: "ActionId",
			headerName: "#",
			flex: 1,
			maxWidth: 100,
			checkboxSelection: true,
			showDisabledCheckboxes: true,
			sortIndex: 1,
		},
		{
			field: "ActionName",
			headerName: "Name",
			flex: 4,
			sortIndex: 0,

			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.ActionName?.localeCompare(nodeB.data.ActionName);
				}
			},
		},
	];

	const defaultColDef = useMemo(
		() => ({
			resizable: true,
			sortable: true,
			filter: true,
			suppressHeaderMenuButton: true,
		}),
		[],
	);

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows(event);
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			params.api.forEachNode((node) => {
				node.setSelected(
					!!node.data &&
						!isNil(
							actionsOfProfile.find((p) => p.ActionId === node.data.ActionId),
						),
				);
			});
			sortRows(params);
		},
		[sortRows],
	);

	const onGridReady = useCallback(
		(event: GridReadyEvent) => {
			setGridApi(event.api);
		},
		[setGridApi],
	);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>Actions</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextField
						id="outlined-basic"
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={(event) => {
							setFilterInput(event.target.value);
							gridApi?.setGridOption("quickFilterText", event?.target.value);
						}}
						margin="dense"
						style={{ width: "100%" }}
					/>
				</Grid2>
				{/* <Grid2 size={{ md: 6, xs: 12 }} style={{ paddingLeft: 15 }}>
          <TextField
            id="outlined-basic"
            label="Profile name to edit"
            variant="outlined"
            value={profileName}
            fullWidth
            margin="dense"
            autoComplete='off'
            onChange={(e: ChangeEvent) => handleChangeProfielName(e)}
            InputLabelProps={{
              shrink: true,
            }}
            required={true}
            error={profileName.length === 0}
            style={{ width: "100%" }}

          />
        </Grid2> */}
			</Grid2>

			<div style={{ width: "100%", height: "60vh" }}>
				<AgGridReact
					loading={isLoading}
					gridOptions={{
						theme: themeMaterial.withParams({
							spacing: 4,
						}),
					}}
					rowData={formattedRows || []}
					getRowId={(params) => String(params.data.ActionId)}
					onGridReady={onGridReady}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					rowSelection={"multiple"}
					rowMultiSelectWithClick
					onSelectionChanged={onAgGridSelectionChanged}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
